import { useEffect, useState } from 'react'

import { Badge } from '@chakra-ui/react'

import { IncidentQueryResult } from '@/graphql/generated/hooks'
import { IncidentActionType, IncidentStatus } from '@/graphql/generated/schemas'
import { getIncidentStatusColor } from '@/utils/incidents'

import { formatDuration } from '../shared/utils/formatDuration'

type IncidentData = IncidentQueryResult['data']['incident']

interface IncidentStatusBadgeIProps {
  incident: IncidentData
}

function getLatestAcknowledgementTime(incident: IncidentData): string | null {
  if (!incident?.actions) {
    return null
  }

  const acknowledgementActions = incident.actions.filter(
    (action) =>
      action.type === IncidentActionType.AcknowledgementByOperator ||
      action.type === IncidentActionType.AcknowledgementByGuard
  )

  if (acknowledgementActions.length === 0) {
    return null
  }

  // Sort by createdAt descending and get the most recent
  const sorted = [...acknowledgementActions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  return sorted[0].createdAt
}

function useElapsedTime(startTime: string | null): number {
  const [elapsed, setElapsed] = useState(() => {
    if (!startTime) return 0
    return Date.now() - new Date(startTime).getTime()
  })

  useEffect(() => {
    if (!startTime) return

    const updateElapsed = () => {
      setElapsed(Date.now() - new Date(startTime).getTime())
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)

    return () => clearInterval(interval)
  }, [startTime])

  return elapsed
}

export const IncidentStatusBadge = ({
  incident,
}: IncidentStatusBadgeIProps) => {
  const status = incident?.status

  const activeStartTime =
    status === IncidentStatus.Active ? incident?.createdAt : null
  const inProgressStartTime =
    status === IncidentStatus.InProgress
      ? getLatestAcknowledgementTime(incident)
      : null

  const activeElapsed = useElapsedTime(activeStartTime)
  const inProgressElapsed = useElapsedTime(inProgressStartTime)

  if (!status) {
    return null
  }

  const getBadgeContent = () => {
    switch (status) {
      case IncidentStatus.Active:
        return `Active for ${formatDuration(activeElapsed)}`
      case IncidentStatus.InProgress:
        return `In Progress for ${formatDuration(inProgressElapsed)}`
      case IncidentStatus.Resolved:
        return 'Resolved'
      default:
        return null
    }
  }

  const content = getBadgeContent()

  if (!content) {
    return null
  }

  return (
    <Badge
      bgColor={getIncidentStatusColor(status)}
      color='#fff'
      data-testid='incident-drawer-status-badge'
      fontSize='12px'
      fontWeight='medium'
      px='8px'
      py='4px'
      rounded='6px'
      textTransform='none'
    >
      {content}
    </Badge>
  )
}
